<script setup lang="ts">

import {computed} from "vue";
import {format} from "date-fns";
import Icon from "@/components/UI/Icon.vue";
import {useRouter} from "vue-router";

const props = defineProps<{
  isRecurrent?: boolean,
  price: number,
  expiredDate: Date
  card?: string,
  duration?: number
}>()
const date = computed<string>(() => format(props.expiredDate, 'dd.MM.yyyy'));
const emit = defineEmits(['support'])
const openSupportChat = () => {
  emit('support')
}
const months = computed(() => {
  if (props.duration === 1) return 'мес';
  if (props.duration === 12 ) return 'год';
  return props.duration+'мес';
});
</script>

<template>
  <div class="subscription">
    <div class="subscription__header">
      <div class="subscription__header__left">ПОДПИСКА PRO</div>
      <div class="subscription__header__right">Активно</div>
    </div>
    <div class="subscription__content">
      <div class="subscription__content__column">
        <div>{{ isRecurrent ? 'Дата платежа:' : 'Действует до:' }}</div>
        <div>{{ date }} г.</div>
      </div>
      <div class="subscription__content__column">
        <div>Стоимость:</div>
        <div>{{ price?.toLocaleString('ru-RU') }} ₽/{{months}}.</div>
      </div>
    </div>
    <div class="subscription__info">
      <template v-if="isRecurrent">
        <div>
          <div class="status-button green">
            <Icon name="check" :size="7"/>
          </div>
        </div>
        <div>Подписка продлевается автоматически в дату платежа с карты *{{ card }}.</div>
      </template>
      <template v-else>
        <div>
          <div class="status-button red">
            <Icon name="close" :size="7"/>
          </div>
        </div>
        <div>Автоматическое продление выключено. Вы можете продлить по завершению текущей подписки.</div>
      </template>
    </div>
    <div class="subscription__footer">
      <template v-if="isRecurrent">
        <div class="button button__dark" @click="openSupportChat">Техническая поддержка</div>
      </template>
      <template v-else>
        <div class="button button__gray">Возобновить подписку</div>
        <div class="button button__outline" @click="openSupportChat">Техническая поддержка</div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.status-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: #9A9B9C;
  border-radius: 50%;
  line-height: 0;

  &.red {
    background-color: #FFD0CA;
  }

  &.green {
    background-color: #D5EDBD;
  }
}

.button {
  height: 48px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 12px;
  line-height: 12px;

  &__dark {
    background-color: #232529;
    color: #F5F5F5;
  }

  &__blue {
    background-color: #6C7BB2;
    color: #F5F5F5;
  }

  &__gray {
    background-color: #B7B7B8;
    color: #F5F5F5;
  }

  &__outline {
    outline: 1px solid #232529;
  }
}

.subscription {
  background-color: #FFFFFF;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__left {
      font-weight: 700;
      font-size: 20px;
      line-height: 20px;
    }

    &__right {
      background: linear-gradient(91.59deg, #FFBA35 0%, #FD9F30 100%);
      width: 88px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 14px;
      font-size: 12px;
      line-height: 12px;
      color: #F5F5F5;
    }
  }

  &__content {
    height: 80px;
    box-sizing: border-box;
    display: flex;
    border-radius: 10px;
    box-shadow: 0 0 20px 0 #0000000F;
    font-weight: 500;
    font-size: 14px;
    line-height: 14px;
    padding: 20px 25px;
    justify-content: space-between;
    position: relative;

    &:before {
      content: "";
      display: block;
      width: 1px;
      height: 40px;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translateY(-50%);
      background-color: #EFEFEF;
    }

    &__column {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      text-align: center;
      gap: 8px;

      > div:first-child {
        color: #9A9B9C;
        font-size: 12px;
        line-height: 12px;
      }
    }
  }

  &__info {
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    line-height: 15px;
  }

  &__footer {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
